import { breakpoints } from '@msc-b2c/shared-utils';

// Define options for Intersection Observer
const observerOptions: IntersectionObserverInit = {
  root: null, // Use viewport as root
  threshold: 0.62,
  //left: cause the first column has position sticky
  rootMargin: '0px 0px 0px -200px',
};

const createCarouselActions = (
  table: HTMLElement,
  length: number,
  isIndicatorsNeeded: boolean
): void => {
  //Section to add carousel actions markup \\
  const actionsDiv: HTMLDivElement = document.createElement('div');
  actionsDiv.classList.add('cmp-carousel__actions');

  const beforeBtn: HTMLButtonElement = document.createElement('button');
  beforeBtn.classList.add('cmp-carousel__action');
  beforeBtn.classList.add('cmp-carousel__action--previous');
  const beforeIcon: HTMLSpanElement = document.createElement('span');
  beforeIcon.classList.add('cmp-carousel__action-icon');
  beforeBtn.appendChild(beforeIcon);

  const afterBtn: HTMLButtonElement = document.createElement('button');
  afterBtn.classList.add('cmp-carousel__action');
  afterBtn.classList.add('cmp-carousel__action--next');
  const afterIcon: HTMLSpanElement = document.createElement('span');
  afterIcon.classList.add('cmp-carousel__action-icon');
  afterBtn.appendChild(afterIcon);

  if (isIndicatorsNeeded) {
    const controlDiv: HTMLDivElement = document.createElement('div');
    controlDiv.classList.add('cmp-carousel__control-indicator-background');
    const controlIndicatorList: HTMLOListElement = document.createElement('ol');
    controlIndicatorList.classList.add('cmp-carousel__indicators');

    // i=1 cause the first row is the header
    for (let i = 1; i < length; i++) {
      const li = document.createElement('li');
      li.classList.add('cmp-carousel__indicator');
      li.setAttribute('data-row', `row-${i}`);

      controlIndicatorList.appendChild(li);
    }
    controlDiv.appendChild(controlIndicatorList);
    actionsDiv.appendChild(beforeBtn);
    actionsDiv.appendChild(controlDiv);
    actionsDiv.appendChild(afterBtn);
    table.appendChild(actionsDiv);
  } else {
    actionsDiv.appendChild(beforeBtn);
    actionsDiv.appendChild(afterBtn);
    table.appendChild(actionsDiv);
  }
};

const actionButtonFunctionality = (
  button: HTMLButtonElement,
  headerRow: HTMLElement,
  table: HTMLElement
) => {
  const row = button.getAttribute('data-row');
  const rowElement = table.querySelector(`#${row}`);
  const { right: headerRight } = headerRow?.getBoundingClientRect() as DOMRect;

  if (rowElement) {
    // Get the bounding box for the row element
    const { left } = rowElement.getBoundingClientRect();

    // Calculate the offset so the row element will be positioned at the right edge of the header row
    const scrollTarget = left - headerRight;

    // Scroll to the calculated position smoothly
    table.scrollBy({
      left: scrollTarget,
      behavior: 'smooth',
    });
  }
};

export const packageContainerLogic = (container: HTMLElement): void => {
  const teaserBtn: HTMLElement | null = container.querySelector(
    '.cmp-teaser__button-wrapper .cmp-teaser__button'
  );
  const textWithTable: HTMLElement | null = container.querySelector('.cmp-text:has(table)');
  const table = textWithTable?.querySelector('table');
  if (!table || !(table instanceof HTMLTableElement)) {
    console.error('Table element is missing or not a valid HTMLTableElement');
    return;
  }

  const rows = table.querySelectorAll('tr');
  const lastRow = rows[rows.length - 1]?.children;
  const headerRow = table.querySelector('tr > td') as HTMLElement;

  if (!textWithTable || !table || !teaserBtn || !lastRow || !headerRow) {
    console.error('Missing elements in packageContainer');
    return;
  }

  const equalizeTrHeights = (table: HTMLTableElement): void => {
    const rows = table.querySelectorAll('tr');
    let maxRowHeight = 0;
    rows.forEach((row) => {
      row.style.height = 'auto';
      maxRowHeight = Math.max(maxRowHeight, row.offsetHeight);
    });
    rows.forEach((row) => {
      row.style.height = `${maxRowHeight}px`;
    });
  };

  // check if table inside container is overflowing
  // if yes make it continue without the right padding
  // to indicate that there is more content to show
  function setMarginOnTableScroll(packageContainer: Element, textWithTable: HTMLElement) {
    let overflowVisible = false;

    const tableWrapper = textWithTable.querySelector('.table-wrapper');
    if (!tableWrapper) return;

    const updateState = () => {
      const maxScrollLeft = tableWrapper.scrollWidth - tableWrapper.clientWidth;
      const hasOverflow = maxScrollLeft > 0;

      if (!hasOverflow) {
        overflowVisible = false;
        packageContainer.classList.remove('-table-overflows');
        return;
      }

      // User reached the end
      if (overflowVisible && tableWrapper.scrollLeft >= maxScrollLeft - 20) {
        overflowVisible = false;
        packageContainer.classList.remove('-table-overflows');
        return;
      }

      // User moved away from the end
      if (!overflowVisible && tableWrapper.scrollLeft < maxScrollLeft - 120) {
        overflowVisible = true;
        packageContainer.classList.add('-table-overflows');
      }
    };

    requestAnimationFrame(updateState);

    const resizeObserver = new ResizeObserver(updateState);
    resizeObserver.observe(tableWrapper);

    tableWrapper.addEventListener('scroll', updateState, {
      passive: true,
    });

    window.addEventListener('resize', updateState);

    return () => {
      resizeObserver.disconnect();
      tableWrapper.removeEventListener('scroll', updateState);
      window.removeEventListener('resize', updateState);
    };
  }

  // Mobile tabs: show only the first column and the selected column
  function enableMobileTabs(table: HTMLTableElement) {
    if (table.parentNode?.querySelector('.mobile-tabs-container')) return;

    const firstRow = table.querySelector('tr');
    if (!firstRow) return;
    const headerCells = firstRow.querySelectorAll('th, td');
    if (headerCells.length <= 1) return;

    const tabsContainer = document.createElement('div');
    tabsContainer.classList.add('mobile-tabs-container');
    tabsContainer.setAttribute('role', 'tablist');

    for (let colIndex = 1; colIndex < headerCells.length; colIndex++) {
      const columnHeader = headerCells[colIndex].textContent ?? `Tab ${colIndex}`;
      const tabButton = document.createElement('button');
      tabButton.textContent = columnHeader;
      tabButton.classList.add('mobile-tab-button');
      tabButton.dataset['colIndex'] = colIndex.toString();
      tabButton.setAttribute('role', 'tab');
      tabsContainer.appendChild(tabButton);
    }

    tabsContainer.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName.toLowerCase() === 'button' &&
        target.classList.contains('mobile-tab-button')
      ) {
        tabsContainer.querySelectorAll('button').forEach((btn) => btn.classList.remove('active'));
        target.classList.add('active');
        const colIndex = parseInt(target.dataset['colIndex'] ?? '1', 10);
        showOnlyColumn(table, colIndex);
      }
    });

    table.parentNode?.insertBefore(tabsContainer, table);

    const firstTabButton = tabsContainer.querySelector('button');
    if (firstTabButton) {
      firstTabButton.classList.add('active');
      showOnlyColumn(table, parseInt(firstTabButton.dataset['colIndex'] ?? '1', 10));
    }
  }

  // wrap table so we make this the scrollable container
  // so only table can scroll
  function wrapTable(textWithRable: Element) {
    const table = textWithRable.querySelector('table');

    if (table) {
      const wrapper = document.createElement('div');
      wrapper.className = 'table-wrapper';

      table.parentNode?.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  //get the text with direct child table
  function getTextWithDirectChildTable(packageContainer: Element) {
    const textThatHasDirectChildTable = packageContainer.querySelector(
      '.text > .cmp-text > table'
    )?.parentElement;

    return textThatHasDirectChildTable ? textThatHasDirectChildTable : null;
  }

  // Hide all columns except the first and the selected one
  function showOnlyColumn(table: HTMLTableElement, colToShow: number) {
    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      const cells = row.querySelectorAll('th, td');
      cells.forEach((cell, colIndex) => {
        (cell as HTMLElement).style.display =
          colIndex === 0 || colIndex === colToShow ? '' : 'none';
      });
    });
  }

  for (let i = 1; i < lastRow.length; i++) {
    lastRow[i].id = `row-${i}`;
  }
  equalizeTrHeights(table);

  const textThatHasDirectChildTable = getTextWithDirectChildTable(container);

  if (
    textThatHasDirectChildTable &&
    textThatHasDirectChildTable?.parentElement?.classList.contains('-is-fixed-layout')
  ) {
    wrapTable(textThatHasDirectChildTable);
    setMarginOnTableScroll(container, textThatHasDirectChildTable);
  }

  const isMobile = window.innerWidth < breakpoints.md;

  // Conditionally initialize navigation based on viewport width
  if (isMobile) {
    enableMobileTabs(table);
    textWithTable.style.overflowX = 'hidden';
  } else {
    createCarouselActions(textWithTable, lastRow.length, true);
    const actionsDiv = container.querySelector('.cmp-carousel__actions')!;
    const indicators = actionsDiv.querySelectorAll('.cmp-carousel__indicator');
    const prevBtn: HTMLButtonElement | null = actionsDiv.querySelector(
      '.cmp-carousel__action--previous'
    );
    const nextBtn: HTMLButtonElement | null = actionsDiv.querySelector(
      '.cmp-carousel__action--next'
    );

    if (!indicators) {
      console.error('Missing indicators in packageContainer');
      return;
    }

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        const cardId = entry.target.id;
        const indicator = document.querySelector<HTMLSpanElement>(
          `.cmp-carousel__indicator[data-row="${cardId}"]`
        );
        if (entry.isIntersecting && indicator) {
          Array.from(indicators).forEach((ind) =>
            ind.classList.remove('cmp-carousel__indicator--active')
          );
          indicator.classList.add('cmp-carousel__indicator--active');
          const id = Number(cardId.split('-')[1]);
          prevBtn?.setAttribute('data-row', `row-${id - 1}`);
          nextBtn?.setAttribute('data-row', `row-${id + 1}`);
          if (cardId === 'row-1') {
            prevBtn?.classList.add('cmp-carousel-disableArrowNotIfinity');
          } else {
            prevBtn?.classList.remove('cmp-carousel-disableArrowNotIfinity');
          }
          if (cardId === `row-${lastRow.length - 1}`) {
            nextBtn?.classList.add('cmp-carousel-disableArrowNotIfinity');
          } else {
            nextBtn?.classList.remove('cmp-carousel-disableArrowNotIfinity');
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    Array.from(lastRow).forEach((row) => observer.observe(row));

    prevBtn?.addEventListener('click', () => {
      actionButtonFunctionality(prevBtn, headerRow, textWithTable);
    });
    nextBtn?.addEventListener('click', () => {
      actionButtonFunctionality(nextBtn, headerRow, textWithTable);
    });
  }

  teaserBtn?.addEventListener('click', (e) => {
    e.stopImmediatePropagation();
    container.classList.toggle('expanded');
  });
};

export const comparePackageLogic = (container: HTMLElement): void => {
  const text: HTMLElement | null = container.querySelector('.text:has(table)')!;
  const textWithTable: HTMLElement | null = container.querySelector('.cmp-text:has(table)')!;
  const table: HTMLElement | null | undefined = textWithTable?.querySelector('table')!;
  const allRows = table?.querySelectorAll('tr')!;
  const secondRowCells = allRows[1]?.children!;
  const firstRowCells = allRows[0]?.children!;
  const buttons = container.querySelectorAll('.cmp-container > .button');
  const openingButton = buttons[0];
  const closingButton = buttons[1];

  //Section to add ids to the rows extept the first one \\
  for (let i = 1; i < firstRowCells.length; i++) {
    const spanIndicator = document.createElement('span');
    spanIndicator.innerText = `${i}/${firstRowCells.length - 1}`;
    firstRowCells[i].appendChild(spanIndicator);
    firstRowCells[i].id = `row-${i}`;
    secondRowCells[i].setAttribute('data-row', `row-${i}`);
  }

  const activeRowNumber = 1;
  let activeRow: HTMLTableElement | null = container.querySelector(`#row-${activeRowNumber}`);
  activeRow?.classList.add('active');

  const containerAppliedClasses = Array.from(container.classList);
  const maxHeightClass = containerAppliedClasses.filter((className) =>
    className.startsWith('max-height-')
  );
  const maxHeight = maxHeightClass.length > 0 ? maxHeightClass[0].split('-')[2] : undefined;

  if (maxHeight) {
    setTimeout(() => {
      const calcMaxHeight = text.offsetHeight * (parseFloat(maxHeight) / 100);
      text.style.gridTemplateRows = `${calcMaxHeight}px`;
    }, 1000);
  } else {
    (buttons[0] as HTMLElement).style.display = 'none';
  }

  //Section to add carousel actions markup \\
  createCarouselActions(table, firstRowCells.length, false);

  const actionsDiv = container.querySelector('.cmp-carousel__actions')!;
  const prevBtn: HTMLButtonElement | null = actionsDiv.querySelector(
    '.cmp-carousel__action--previous'
  );
  const nextBtn: HTMLButtonElement | null = actionsDiv.querySelector('.cmp-carousel__action--next');

  const observerCallback: IntersectionObserverCallback = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const cardId = entry.target.getAttribute('data-row');
      if (entry.isIntersecting) {
        activeRow?.classList.remove('active');

        activeRow = container.querySelector(`#${cardId}`)!;

        activeRow.classList.add('active');

        if (cardId === 'row-1') {
          prevBtn?.classList.add('cmp-carousel-disableArrowNotIfinity');
        } else {
          prevBtn?.classList.remove('cmp-carousel-disableArrowNotIfinity');
        }

        if (cardId === `row-${firstRowCells.length - 1}`) {
          nextBtn?.classList.add('cmp-carousel-disableArrowNotIfinity');
        } else {
          nextBtn?.classList.remove('cmp-carousel-disableArrowNotIfinity');
        }
      }
    });
  };

  // Create the observer instance with the specified callback and options
  const observer = new IntersectionObserver(observerCallback, observerOptions);

  // Observe each card
  Array.from(secondRowCells)
    .slice(1)
    .forEach((row) => observer.observe(row));

  prevBtn?.addEventListener('click', () => {
    textWithTable.scrollBy({
      left: -activeRow?.offsetWidth! / 2,
      behavior: 'smooth',
    });
  });

  // Add click listener to simulate a horizontal scroll
  nextBtn?.addEventListener('click', () => {
    textWithTable.scrollBy({
      left: 20,
      behavior: 'smooth',
    });
  });

  openingButton?.addEventListener('click', () => {
    container.classList.add('expanded');
    if (maxHeight) {
      text.style.gridTemplateRows = `${table.offsetHeight}px`;
    }
  });

  closingButton?.addEventListener('click', () => {
    container.classList.remove('expanded');
    if (maxHeight) {
      const calcMaxHeight = text.offsetHeight * (parseFloat(maxHeight) / 100);
      text.style.gridTemplateRows = `${calcMaxHeight}px`;
    }
  });
};
